import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import "@phosphor-icons/webcomponents/PhCamera";
import "@phosphor-icons/webcomponents/PhCheck";
import "@phosphor-icons/webcomponents/PhX";
import "@phosphor-icons/webcomponents/PhWarning";
import "ui-components/button/wow-button";
import "ui-components/form-elements/switch/switch";
import "ui-components/modal/wow-modal";

import { styles } from "./stream-config-modal.css";

type AspectRatio = "landscape" | "portrait";
type PermissionState = "prompt" | "granted" | "denied" | "checking";

@customElement("stream-config-modal")
export class StreamConfigModal extends LitElement {
  static override styles = [styles];

  @property({ type: String })
  streamId!: string;

  @state()
  private aspectRatio: AspectRatio = "landscape";

  @state()
  private enableAudio = true;

  @state()
  private permissionState: PermissionState = "checking";

  override connectedCallback() {
    super.connectedCallback();
    this.setDefaultAspectRatio();
    void this.checkCameraPermission();
  }

  private setDefaultAspectRatio(): void {
    const isPortrait = window.innerHeight > window.innerWidth;
    this.aspectRatio = isPortrait ? "portrait" : "landscape";
  }

  private async checkCameraPermission(): Promise<void> {
    try {
      const cameraPermission = await navigator.permissions.query({ name: "camera" as PermissionName });
      this.permissionState = cameraPermission.state as PermissionState;

      cameraPermission.addEventListener("change", () => {
        this.permissionState = cameraPermission.state as PermissionState;
      });
    } catch {
      this.permissionState = "prompt";
    }
  }

  private handleSelectLandscape = (): void => {
    this.aspectRatio = "landscape";
  };

  private handleSelectPortrait = (): void => {
    this.aspectRatio = "portrait";
  };

  private handleAudioChange(e: CustomEvent): void {
    this.enableAudio = e.detail.checked;
  }

  private handleCancel(): void {
    this.dispatchEvent(new CustomEvent("cancel", { bubbles: true, composed: true }));
  }

  private handleStart(): void {
    this.dispatchEvent(
      new CustomEvent("start-stream", {
        detail: {
          aspectRatio: this.aspectRatio,
          enableAudio: this.enableAudio,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleClose(): void {
    this.handleCancel();
  }

  private renderPermissionStatus() {
    const statusClasses = {
      "permission-status": true,
      [this.permissionState]: true,
    };

    switch (this.permissionState) {
      case "checking":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-camera size="20"></ph-camera>
            <span>Checking camera permissions...</span>
          </div>
        `;
      case "granted":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-check size="20" weight="bold"></ph-check>
            <span>Camera access granted</span>
          </div>
        `;
      case "denied":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-x size="20" weight="bold"></ph-x>
            <span>Camera access denied. Please enable in browser settings.</span>
          </div>
        `;
      case "prompt":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-warning size="20"></ph-warning>
            <span>Browser will ask permission to use camera</span>
          </div>
        `;
    }
  }

  override render() {
    const landscapeClasses = {
      "aspect-option": true,
      selected: this.aspectRatio === "landscape",
    };

    const portraitClasses = {
      "aspect-option": true,
      selected: this.aspectRatio === "portrait",
    };

    return html`
      <wow-modal prevent-close @close=${this.handleClose}>
        <span slot="header">Configure your stream</span>

        <div class="config-content">
          <div class="section">
            <label class="section-label">Select aspect ratio</label>
            <div class="aspect-ratio-options">
              <div class=${classMap(landscapeClasses)} @click=${this.handleSelectLandscape}>
                <div class="aspect-preview landscape"></div>
                <span class="aspect-label">Landscape (16:9)</span>
              </div>
              <div class=${classMap(portraitClasses)} @click=${this.handleSelectPortrait}>
                <div class="aspect-preview portrait"></div>
                <span class="aspect-label">Portrait (9:16)</span>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="audio-section">
              <span class="audio-label">Stream with sound</span>
              <wow-switch .checked=${this.enableAudio} @change=${this.handleAudioChange}></wow-switch>
            </div>
          </div>

          <div class="section">${this.renderPermissionStatus()}</div>

          <div class="modal-actions">
            <wow-button variant="neutral" @click=${this.handleCancel}>Cancel</wow-button>
            <wow-button variant="primary" @click=${this.handleStart} ?disabled=${this.permissionState === "denied"}>
              Start streaming
            </wow-button>
          </div>
        </div>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "stream-config-modal": StreamConfigModal;
  }
}
